import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import { Theme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Room from "@material-ui/icons/Room";
import { createStyles, makeStyles } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  AvailabilityType,
  useGetExpertAvailabilityQuery,
  useUpdateAvailabilityMutation,
} from "../../api/rtk/ExpertAvailabilityApi";
import { useGetMeAsExpertQuery } from "../../api/rtk/MeApi";
import { ExamDayViewModel, getExamIdsOfExamDay } from "../../model/ExamDayViewModel";
import { LocationViewModel } from "../../model/LocationViewModel";
import { routesPaths } from "../../Routes";
import { TranslationKey } from "../../translations/TranslationKey";
import { areDatesEqual, isDateInArray } from "../../utils/Dates";
import { navigateBack } from "../../utils/Navigation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import LocaleFormatDate from "../common/dates/LocaleFormatDate";
import TitleWithIcon from "../common/TitleWithIcon";
import TranslatedMessage from "../translations/TranslatedMessage";
import ExpertAvailabilityRow, { CellData, EmptyCellData, FullCellData } from "./ExpertAvailabilityRow";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import moment from "moment";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    paper: {
      marginTop: theme.spacing(3),
      width: "100%",
      maxHeight: "calc(100vh - 230px)",
      overflowX: "auto",
      overflowY: "auto",
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 650,
    },
  })
);

interface DayColumn {
  date: Date;
  cells: CellData[];
}

interface LocationRow {
  location: LocationViewModel;
  cells: CellData[];
}

interface ExpertAvailabilitySetupTypeProps {
  title: TranslationKey;
  availabilityType: AvailabilityType;
}

type ExpertAvailabilitySetupProps = ExpertAvailabilitySetupTypeProps;

const ExpertAvailabilitySetup: React.FunctionComponent<ExpertAvailabilitySetupProps> = ({
  title,
  availabilityType,
}) => {
  const { examSessionId } = useParams<{ examSessionId: string }>();

  const { data: expert, isFetching: isFetchingExpert } = useGetMeAsExpertQuery();
  const { data: initialExpertAvailability, isFetching: isFetchingAvailbability } = useGetExpertAvailabilityQuery({
    examSessionId,
    availabilityType,
  });
  const [updateAvailability, { isLoading: isUpdateAvailabilityLoading }] = useUpdateAvailabilityMutation();
  const [selectedExamDayIds, setSelectedExamDayIds] = useState<string[]>([]);
  const history = useHistory();
  const classes = useStyles({});

  useEffect(() => {
    if (initialExpertAvailability) setSelectedExamDayIds(initialExpertAvailability.selectedExamDayIds);
  }, [initialExpertAvailability]);

  if (isFetchingExpert || isFetchingAvailbability || isUpdateAvailabilityLoading) {
    return <LinearProgress variant="query" />;
  }

  if (!expert || !initialExpertAvailability || !examSessionId) return <></>;
  
  if (initialExpertAvailability.expertAvailabilitySurveyDeadline === null || moment(initialExpertAvailability.expertAvailabilitySurveyDeadline).isBefore(moment())) {
    return (
      <React.Fragment>
        <Alert severity="info">
          <AlertTitle>
            <TranslatedMessage id="common.info" />
          </AlertTitle>
          <TranslatedMessage id="expertAvailability.surveyDeadlinePassed" />
        </Alert>
      </React.Fragment>
    );
  }

  const { availableExamDays, locations } = initialExpertAvailability;

  if (availableExamDays.length === 0) {
    return (
      <React.Fragment>
        <Alert severity="info">
          <AlertTitle>
            <TranslatedMessage id="common.info" />
          </AlertTitle>
          <TranslatedMessage id="expertAvailability.noDatesAvailable" />
        </Alert>
      </React.Fragment>
    );
  }

  const getAllAvailableDays = () => {
    const dates = availableExamDays.map((day) => {
      const date = new Date(day.date);
      date.setHours(0, 0, 0, 0);
      return date;
    });
    const uniqueDates: Date[] = [];
    for (let date of dates) {
      if (!isDateInArray(date, uniqueDates)) {
        uniqueDates.push(date);
      }
    }
    return uniqueDates.sort((a: Date, b: Date) => a.getTime() - b.getTime());
  };

  const getCellDataForExamDay = (examDay?: ExamDayViewModel) => {
    if (!examDay) return new EmptyCellData();
    const examIds = getExamIdsOfExamDay(examDay);

    if (examIds.length === 0) return new EmptyCellData();
    const isChecked = selectedExamDayIds.includes(examDay.id);
    return new FullCellData(isChecked, examDay, examIds);
  };

  const locationsCount = locations.length;
  const allAvailableDates = getAllAvailableDays();

  function getColumns(): DayColumn[] {
    const columns = allAvailableDates.map((date) => {
      const columnCells = new Array(locationsCount);
      locations.forEach((location, index) => {
        const currentExamDay = availableExamDays
          .filter((day) => day.locationId === location.id)
          .find((e) => areDatesEqual(e.date, date));

        const cellData = getCellDataForExamDay(currentExamDay);
        columnCells[index] = cellData;
      });
      return { date, cells: columnCells };
    });
    return columns.filter((column) => {
      const allCellsEmpty = column.cells.every((cell) => cell instanceof EmptyCellData);
      return !allCellsEmpty;
    });
  }

  let columns = getColumns();

  const getRows = () => {
    const rows: LocationRow[] = new Array(locationsCount); //vertical array of rows, where each row as array of horizontal celldata
    for (let i = 0; i < locationsCount; i++) {
      rows[i] = { location: locations[i], cells: new Array(columns.length) };
    }
    //transform columns to rows
    columns.forEach((col, horizontalIndex) => {
      col.cells.forEach((cell, verticalCellIndex) => {
        rows[verticalCellIndex].cells[horizontalIndex] = cell;
      });
    });

    // remove rows with all empty cells
    return rows.filter((row) => !row.cells.every((cell) => cell instanceof EmptyCellData));
  };

  const handleSave = async () => {
    const expertAvailability = { ...initialExpertAvailability, selectedExamDayIds };
    const response = await updateAvailability({ examSessionId, expertAvailability, availabilityType }).unwrap();
    if (response.success) {
      navigateBack(routesPaths.root(), history, history.location);
    }
  };

  return (
    <React.Fragment>
      <Grid container={true} spacing={0} alignItems="baseline">
        <Grid item={true} xs={12} sm={6}>
          <TitleWithIcon title={<TranslatedMessage id={title} />} variant="h4">
            <Room fontSize="large" />
          </TitleWithIcon>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <DefaultButtons
            onSave={handleSave}
            isSaveButtonVisible={true}
            isDeleteButtonVisible={false}
            isCloseButtonVisible={false}
          />
        </Grid>
      </Grid>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Table stickyHeader={true} className={classes.table} aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((col) => (
                  <TableCell align="right" key={col.date.getTime()}>
                    <LocaleFormatDate date={col.date} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {getRows().map((row, index) => (
                <ExpertAvailabilityRow
                  key={row.location.id}
                  location={row.location}
                  cells={row.cells}
                  selectedExamDayIds={selectedExamDayIds}
                  availableExamDays={availableExamDays}
                  allSessionExams={initialExpertAvailability.exams}
                  onChange={setSelectedExamDayIds}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>
    </React.Fragment>
  );
};

export default ExpertAvailabilitySetup;
